import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Play, Youtube } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const getItemsPerView = (width) => {
  if (width >= 1280) return 3;
  if (width >= 768) return 2;
  return 1;
};

const VideoCard = ({ video, index = 0 }) => {
  return (
    <Card className="group relative cursor-pointer overflow-hidden rounded-lg border-0 shadow-lg transition-shadow duration-200 hover:shadow-xl">
      <div className="relative aspect-video overflow-hidden bg-gray-900">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="h-full w-full object-cover"
          loading={index < 3 ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={index === 0 ? 'high' : 'auto'}
        />

        <div className="absolute inset-0 bg-black/40 transition-colors duration-200 group-hover:bg-black/55" />

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 transition-colors duration-200 group-hover:bg-white">
            <Play className="ml-1 h-10 w-10 text-gray-900" fill="currentColor" />
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="mb-2 text-xl font-bold text-white">{video.title}</h3>
          <p className="text-sm text-white/90">{video.type}</p>
        </div>
      </div>
    </Card>
  );
};

const VideoStoriesSection = ({ videos }) => {
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const itemsPerView = getItemsPerView(viewportWidth);
  const maxIndex = Math.max(0, videos.length - itemsPerView);

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [maxIndex]);

  const trackStyle = useMemo(
    () => ({
      width: `${(videos.length / itemsPerView) * 100}%`,
      transform: `translateX(-${(currentIndex * 100) / videos.length}%)`
    }),
    [videos.length, currentIndex, itemsPerView]
  );

  const openVideo = (url) => {
    window.open(url, '_blank');
  };

  const goPrev = () => setCurrentIndex((prev) => Math.max(0, prev - 1));
  const goNext = () => setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));

  return (
    <section id="videos" className="perf-section bg-gray-50 py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-16 space-y-4 text-center">
          <span className="text-sm font-medium uppercase tracking-widest text-gray-500">Video Stories</span>
          <h2 className="text-5xl font-bold text-gray-900 md:text-6xl">Cinematic Wedding Films</h2>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            Watch our collection of beautiful wedding trailers and short reels
          </p>
        </div>

        <div className="relative">
          <Button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="absolute left-2 top-1/2 z-10 h-12 w-12 -translate-y-1/2 rounded-full bg-gray-900 p-0 text-white shadow-2xl hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40 md:-left-6 md:h-14 md:w-14"
            variant="default"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>

          <Button
            onClick={goNext}
            disabled={currentIndex >= maxIndex}
            className="absolute right-2 top-1/2 z-10 h-12 w-12 -translate-y-1/2 rounded-full bg-gray-900 p-0 text-white shadow-2xl hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40 md:-right-6 md:h-14 md:w-14"
            variant="default"
          >
            <ChevronRight className="h-6 w-6" />
          </Button>

          <div className="overflow-hidden">
            <div className="transition-transform duration-500 ease-out" style={trackStyle}>
              <div className="flex">
                {videos.map((video, index) => (
                  <div
                    key={video.id}
                    className="shrink-0 px-3"
                    style={{ width: `${100 / videos.length}%` }}
                    onClick={() => openVideo(video.url)}
                  >
                    <VideoCard video={video} index={index} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {videos.length > itemsPerView && (
            <div className="mt-8 flex justify-center gap-2">
              {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                <button
                  key={`video-dot-${index}`}
                  type="button"
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2.5 rounded-full transition-all ${
                    currentIndex === index ? 'w-8 bg-gray-900' : 'w-2.5 bg-gray-300'
                  }`}
                  aria-label={`Go to video slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-12 text-center">
          <Button
            onClick={() => window.open('https://www.youtube.com/@sagrsen', '_blank')}
            className="bg-red-600 px-8 py-6 text-lg text-white hover:bg-red-700"
          >
            <Youtube className="mr-2 h-5 w-5" />
            View More on YouTube
          </Button>
        </div>
      </div>
    </section>
  );
};

export default VideoStoriesSection;
